import React from 'react';
import { createRoot } from 'react-dom/client';
import { createInertiaApp } from '@inertiajs/react';
import { Pages } from './Pages/index.jsx';
import { initSentry, SentryErrorBoundary } from './sentry.js';

initSentry();

// Every layout loads this bundle, but only inertia.html.erb emits an Inertia
// root (`<div id="app" data-page="...">`). The plain ERB pages — the instructor
// dashboard, the reviewer dashboard, the Doorkeeper screens — have nothing to
// mount into, and createInertiaApp would throw reading `.component` off the
// null initial page it recovers from the DOM. Skip the boot on those pages.
const inertia_root = document.getElementById('app');

if (inertia_root) {
  // A root with no payload is a broken Inertia response, not an ERB page. Say
  // so loudly rather than quietly rendering an empty shell.
  if (!inertia_root.dataset.page) {
    throw new Error('Inertia root #app is present but has no data-page payload');
  }

  createInertiaApp({
    resolve: name => Pages[name],
    setup({ el, App, props }) {
      createRoot(el).render(
        <SentryErrorBoundary
          fallback={({ error, resetError }) => (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
              <div className="max-w-md w-full bg-white border border-rose-200 rounded-2xl p-6 shadow-sm">
                <h1 className="text-lg font-bold text-rose-700">Something went wrong</h1>
                <p className="mt-2 text-sm text-gray-600">
                  The page hit an error. The team has been notified.
                </p>
                {error?.message && (
                  <pre className="mt-3 text-xs text-gray-500 whitespace-pre-wrap break-all">{error.message}</pre>
                )}
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={resetError}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                  >
                    Try again
                  </button>
                  <a
                    href="/"
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Go home
                  </a>
                </div>
              </div>
            </div>
          )}
        >
          <App {...props} />
        </SentryErrorBoundary>
      );
    },
    progress: false,
  });
}
